import { AuthModel } from "./../modules/auth/core/_models";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import dayjs from "dayjs";
import jwtDecode, { JwtPayload } from "jwt-decode";

import { useAuth } from "@/modules/auth";
import { getRuntimeConfig } from "@/config/runtimeConfig";

import {
	GET_USER_BY_ACCESSTOKEN_URL,
	LOGIN_URL,
	REFRESH_TOKEN_URL,
} from "@/modules/auth/core/_requests";

export function useApi() {
	const { auth, saveAuth, setTimer, timerIntervalId, logout } = useAuth();
	const controller = new AbortController();

	function createApi(config?: AxiosRequestConfig) {
		const api = axios.create(config);

		api.interceptors.request.use(requestInterceptor, (err: any) => Promise.reject(err));
		api.interceptors.response.use((response) => response, responseInterceptor);
		return api;
	}

	const api = createApi({ baseURL: getRuntimeConfig().apiUrl });

	function getUserByToken() {
		return api.get<Auth>(GET_USER_BY_ACCESSTOKEN_URL);
	}

	async function refreshToken(refreshToken: string) {
		// Precisa ser o mesmo client que emitiu o token original (password grant
		// ou authorization_code) — a Passport valida isso e rejeita com
		// invalid_grant se o client_id não bater.
		const { passwordGrantClientId } = getRuntimeConfig();
		const clientId = auth?.issued_by_client_id || passwordGrantClientId;

		return api.post<AuthModel>(REFRESH_TOKEN_URL, {
			refresh_token: refreshToken,
			grant_type: "refresh_token",
			client_id: clientId,
			scope: "*",
		});
	}

	function requestInterceptor(config: AxiosRequestConfig): AxiosRequestConfig {
		function abortAndLogout() {
			controller.abort();
			logout();
		}

		if (auth && auth.access_token) {
			if (timerIntervalId.current) {
				clearTimeout(timerIntervalId.current);
				setTimer(0);
			}

			if (!config.headers) {
				config.headers = {};
			}

			config.headers.Authorization = `Bearer ${auth.access_token}`;
			config.signal = controller.signal;

			const tokenData = jwtDecode<JwtPayload>(auth.access_token);
			if (!tokenData.exp) {
				return config;
			}

			const expiresAt = dayjs(tokenData.exp * 1000);
			const now = dayjs();

			if (expiresAt.isBefore(now)) {
				abortAndLogout();
			}

			async function performRefreshToken() {
				try {
					if (!auth?.refresh_token) {
						abortAndLogout();
						return;
					}

					const { data } = await refreshToken(auth.refresh_token);
					data.issued_by_client_id = auth.issued_by_client_id;
					saveAuth(data);

					if (!config.headers) {
						config.headers = {};
					}

					config.headers.Authorization = `Bearer ${data.access_token}`;
				} catch (error: any) {
					console.error(error);
				}
			}

			if (config?.url !== REFRESH_TOKEN_URL && expiresAt.diff(now, "m") <= 10) {
				performRefreshToken();
			}
		}

		return config;
	}

	async function responseInterceptor(error: AxiosError) {
		const originalConfig = error.config;

		if (originalConfig?.url !== LOGIN_URL && error.response) {
			if (error.response.status === 401) {
				logout();
			}
		}

		return Promise.reject(error);
	}

	return {
		createApi,
		requestInterceptor,
		responseInterceptor,
		getUserByToken,
		refreshToken,
		api,
	};
}
